// The casino's client-side slice (casino.md, plan step 0).
//
// The client simulates no hand and rules on no bet. This module holds the
// last three casino frames the wire sent, the moment the table frame
// arrived, and the small pure sums a panel needs to draw a countdown. It
// touches no DOM, so the four fan-out panels and their tests share it.
//
// **A table deadline anchors against the frame, not against a wall
// clock.** `CasinoTable.act_left_sec` states how many real seconds the
// seat at turn had left when the server sent the frame. A reader that
// prints the field alone shows a timer that freezes between frames, and a
// reader that counts down from a stored `Date.now()` drifts when the
// machine's clock is corrected. So `setCasinoTable` stamps
// `performance.now()`, which is monotonic, and `casinoActLeftSec` takes
// the difference. One style write a frame then drains the bar, and the
// panel needs no interval (ui-map-interface.md §10.3).

import { CASINO_TIMEOUT_SLACK_MS } from '../../gen/presentation';
import {
  CasinoGame, CasinoPhase,
  type CasinoAction, type CasinoLobby, type CasinoResult, type CasinoRules, type CasinoSeatResult,
  type CasinoSeatView, type CasinoStake, type CasinoTable,
} from '../../gen/proto/aquaphobia';

/**
 * The whole casino as this client knows it.
 *
 * Every member is the last frame of its kind and nothing is merged: the
 * server sends a whole `CasinoTable` on every change, a whole
 * `CasinoLobby` on its cadence, and one `CasinoResult` a settled hand. A
 * null member means this client has seen no frame of that kind yet, which
 * is also what a player who sits at no table reads.
 */
export interface CasinoSlice {
  /** The last lobby frame: the open tables, the queue and the closed flag. */
  lobby: CasinoLobby | null;
  /** The last table frame, for the table this reader sits at. */
  table: CasinoTable | null;
  /** The last settled hand. The wheel and the reels animate from it. */
  result: CasinoResult | null;
  /**
   * How many result frames this client has seen, from the first one.
   *
   * `CasinoResult.hand_id` cannot answer "is this result NEW": the slot
   * machine holds no table, so every pull can carry one id, and a screen
   * that waited for a different id would hold its handle dead until the
   * act timeout ran out. The counter rises with EVERY result of EVERY
   * game, so a screen takes a reading before it sends an intent and ends
   * its own flight at the first higher reading (R988).
   */
  resultSeq: number;
  /**
   * `performance.now()` at the moment `table` arrived. It is the anchor of
   * every countdown the table states, and 0 while `table` is null.
   */
  tableAtMs: number;
  /**
   * `performance.now()` at the moment `lobby` arrived, and 0 while `lobby`
   * is null.
   *
   * The lobby carries two countdowns of its own — the wait and the fill —
   * and the lobby frame comes on a slow cadence, about one a second. The
   * anchor lets the queue card count on between two frames, exactly as the
   * table's own anchor does for the turn timer (`arcade/queue.ts` counts
   * its bot fill the same way).
   */
  lobbyAtMs: number;
  /**
   * The sum of the reader's own moves over every result this client has
   * seen, in credits (R2115). The panel takes a reading when a casino
   * visit starts, and the visit net is the difference. A dissolved table
   * moves nothing, because every stake came back.
   */
  netSum: number;
  /** How many results held a stake of the reader's own, from the first. */
  played: number;
  /**
   * The hand, as `casinoHandKey`, in which this client sent a FOLD of the
   * reader's own (R2142). The hold'em screen writes it at the send.
   */
  foldSent: string;
  /**
   * The hand, as `casinoHandKey`, whose fold the table made for the
   * reader when the turn timer ran out (R2142), or ''. `foldCasinoTable`
   * writes it.
   */
  timedOut: string;
}

/** The slice a fresh client starts with: no frame of any kind. */
export function newCasinoSlice(): CasinoSlice {
  return {
    lobby: null, table: null, result: null, resultSeq: 0, tableAtMs: 0, lobbyAtMs: 0,
    netSum: 0, played: 0, foldSent: '', timedOut: '',
  };
}

/** The name of one hand of one table: the key of `foldSent` and `timedOut`. */
export function casinoHandKey(t: CasinoTable): string {
  return `${t.id}:${t.handId}`;
}

/**
 * Files one table frame into the slice and stamps its arrival (R2142).
 *
 * Before it replaces the last frame it reads the TIMEOUT FOLD off the
 * pair. The wire has no field for it, and it needs none: the server
 * folds a hand of a connected reader on one path only, the turn timer
 * (`casinoApplyAuto`, casino.md §6). So a frame that shows the reader's
 * seat folded is the timeout fold when four facts hold together: the
 * last frame of the same hand had the reader's live seat at turn, this
 * client sent no FOLD in that hand, and this frame came no earlier than
 * the deadline the last frame stated, less `CASINO_TIMEOUT_SLACK_MS`.
 * The slack covers the whole second the wire rounds the deadline up by,
 * and the lag of the frame.
 */
export function foldCasinoTable(slice: CasinoSlice, t: CasinoTable, atMs: number): void {
  const was = slice.table;
  const key = casinoHandKey(t);
  if (was && casinoHandKey(was) === key) {
    const before = casinoSeatOf(was);
    const now = casinoSeatOf(t);
    const deadlineMs = slice.tableAtMs + was.actLeftSec * 1000;
    if (before && now && before.seat === now.seat && was.turnSeat === before.seat
      && !before.folded && now.folded && slice.foldSent !== key
      && atMs >= deadlineMs - CASINO_TIMEOUT_SLACK_MS) {
      slice.timedOut = key;
    }
  }
  // The server opens the next hand id in the WAITING phase, before the
  // deal, so the timeout stands through that wait and goes when a new
  // hand of the table is dealt (R2142).
  if (slice.timedOut !== '' && slice.timedOut !== key
    && (!slice.timedOut.startsWith(`${t.id}:`) || t.phase !== CasinoPhase.CASINO_PHASE_WAITING)) {
    slice.timedOut = '';
  }
  slice.table = t;
  slice.tableAtMs = atMs;
}

/**
 * Whether the TIME line of a timeout fold stands over `t`: in the hand the
 * table folded, and in the wait before the next deal (R2142).
 */
export function casinoTimedOut(slice: CasinoSlice, t: CasinoTable): boolean {
  return slice.timedOut !== '' && (slice.timedOut === casinoHandKey(t)
    || (t.phase === CasinoPhase.CASINO_PHASE_WAITING && slice.timedOut.startsWith(`${t.id}:`)));
}

/**
 * The real seconds the seat at turn still has, now.
 *
 * `nowMs` is a `performance.now()` reading, which the caller already has
 * in a render pass. The answer is never below zero and never above what
 * the frame stated, so a frame that arrives late cannot print a longer
 * turn than the server gave.
 */
export function casinoActLeftSec(slice: CasinoSlice, nowMs: number): number {
  const table = slice.table;
  if (!table || table.actLeftSec <= 0) return 0;
  const goneSec = Math.max(0, (nowMs - slice.tableAtMs) / 1000);
  return Math.max(0, Math.min(table.actLeftSec, table.actLeftSec - goneSec));
}

/**
 * How full the turn timer stands: 1 at the start of the turn, 0 at its
 * end. The bar is one style write a frame, so the fraction is a pure
 * number here and a width there.
 */
export function casinoActFrac(slice: CasinoSlice, nowMs: number): number {
  const full = slice.table?.actLeftSec ?? 0;
  if (full <= 0) return 0;
  return casinoActLeftSec(slice, nowMs) / full;
}

/**
 * The real seconds before the house fills the empty seats of the queue.
 *
 * The lobby frame states what was left when the server sent it, so the
 * reading takes the difference against the lobby's own anchor. It stops at
 * zero and never runs backwards. A queue the server pushed with no timer
 * at all reads zero, and the card then states no countdown, because a
 * countdown the server cannot keep is worse than none (`arcade/queue.ts`).
 */
export function casinoFillLeftSec(slice: CasinoSlice, nowMs: number): number {
  const left = slice.lobby?.fillLeftSec ?? 0;
  if (left <= 0) return 0;
  const goneSec = Math.max(0, (nowMs - slice.lobbyAtMs) / 1000);
  return Math.max(0, left - goneSec);
}

/**
 * The real seconds this reader has waited in the queue, counted on between
 * the lobby frames from the figure the server counted.
 */
export function casinoWaitedSec(slice: CasinoSlice, nowMs: number): number {
  const waited = slice.lobby?.waitedSec ?? 0;
  if (waited < 0) return 0;
  return waited + Math.max(0, (nowMs - slice.lobbyAtMs) / 1000);
}

/**
 * The reader's own seat, or null when the reader holds no seat.
 *
 * Every panel asks this, so it is written once. The wire marks the seat
 * two ways — `CasinoSeatView.is_you` and `CasinoTable.your_seat` — and
 * the flag is read first, because the server sets it per reader.
 *
 * A seat number counts from 0, so 0 is a real chair. `your_seat` says
 * "nobody" with the seat count itself (R1040), which names no chair, so
 * the test is whether the number finds a seat, never whether it is above
 * zero.
 */
export function casinoSeatOf(table: CasinoTable | null): CasinoSeatView | null {
  if (!table) return null;
  for (const s of table.seats) if (s.isYou) return s;
  for (const s of table.seats) if (s.seat === table.yourSeat) return s;
  return null;
}

/**
 * Every seat this reader holds, by the same two marks. Blackjack seats a
 * split hand beside the first one and marks both `is_you`, so the screen
 * that draws the own hands reads the list; every other screen reads the
 * one seat above.
 */
export function casinoSeatsOf(table: CasinoTable): CasinoSeatView[] {
  const mine = table.seats.filter((s) => s.isYou);
  return mine.length > 0 ? mine : table.seats.filter((s) => s.seat === table.yourSeat);
}

/** True while the frame offers this act, whoever holds the turn (R1000):
 *  the TOP UP of hold'em, which a seat takes between hands, when no seat is
 *  at turn, and a bet in an open betting window. */
export function casinoOffers(table: CasinoTable, action: CasinoAction): boolean {
  return table.allowedActions.includes(action);
}

/** True while this reader holds a seat at the table the frame describes. */
export function casinoIsSeated(table: CasinoTable | null): boolean {
  return casinoSeatOf(table) !== null;
}

/**
 * The stake row of one tier, or null for a tier the rule table does not
 * hold and before the hello arrived.
 *
 * Every screen finds the row by its `tier` and never by its place in
 * `CasinoRules.stakes`, so a pack with a gap in its tiers prices every
 * screen right (R1359).
 */
export function stakeAt(rules: CasinoRules | null, tier: number): CasinoStake | null {
  if (!rules) return null;
  for (const s of rules.stakes) if (s.tier === tier) return s;
  return null;
}

/**
 * The minimum bet of a table: the stake row of its tier first, and the
 * frame's own figure only when the rule table holds no such row (R1359,
 * R3029). Blackjack and roulette read this one rule.
 */
export function tableMinBet(rules: CasinoRules | null, table: CasinoTable): number {
  return stakeAt(rules, table.stakeTier)?.minBet ?? table.minBet;
}

/**
 * The rows of one result that belong to the reader.
 *
 * The server lists a seat only when the seat had a stake on the hand
 * (`casino_settle.go`), so an empty answer means that the reader had no
 * bet. The slot machine holds no table and its one row is always the
 * reader's. A table result counts the seats of the table frame that the
 * reader holds, and a frame of another table counts none.
 */
export function casinoOwnRows(r: CasinoResult, table: CasinoTable | null): CasinoSeatResult[] {
  if (r.game === CasinoGame.CASINO_GAME_SLOTS) return r.seats;
  if (!table || table.id !== r.tableId) return [];
  const mine = new Set(casinoSeatsOf(table).map((s) => s.seat));
  return r.seats.filter((row) => mine.has(row.seat));
}

/**
 * Files one settled hand into the slice: the result, the counter, the
 * running sum of the reader's moves and the count of hands with a stake
 * (R988, R2115). The table frame is read before the caller drops it, so
 * the reader's seat is still known.
 */
export function foldCasinoResult(slice: CasinoSlice, r: CasinoResult): void {
  slice.result = r;
  slice.resultSeq += 1;
  if (r.aborted) return;
  slice.netSum += r.yourDelta;
  if (casinoOwnRows(r, slice.table).length > 0) slice.played += 1;
}
